// material-ui
import { ColorProps } from 'types';

// ==============================|| WEIGHTRAX THEME COLORS ||============================== //
//
// Brand palette for the Weightrax SaaS dashboard.
// See THEME-SPEC.md at the repo root for the canonical brand documentation.
//
// Mapped onto Berry's flat-color shape so it slots into the existing
// `buildPalette(presetColor)` switch in src/themes/palette.jsx.
//
// Brand anchors:
//   navy.900 = #1B3A52  (sidebar, AppBar, brand presence)
//   blue.500 = #0088CC  (interactive accent — primary buttons, focus)
//   ink scale            (typography on light surfaces)
//
// Where Berry expects MUI's primary/secondary axis, we map:
//   primary   → blue.* (interactive accent)
//   secondary → navy.* (brand identity / chrome)

const weightrax: ColorProps = {
  // paper & background
  paper: '#FFFFFF',

  // primary (Weightrax interactive blue)
  primaryLight: '#CCE7F5',
  primary200: '#66B8DD',
  primaryMain: '#0088CC',
  primaryDark: '#006FA8',
  primary800: '#004F7A',

  // secondary (Weightrax brand navy)
  secondaryLight: '#D5DEE6',
  secondary200: '#7E97AC',
  secondaryMain: '#1B3A52',
  secondaryDark: '#112B40',
  secondary800: '#0E2334',

  // success
  successLight: '#C6E5D2',
  success200: '#74BA8E',
  successMain: '#2E7D4E',
  successDark: '#1F5A38',

  // error (used for borders / badges / icons only — never body copy, per spec)
  errorLight: '#F2C2C2',
  errorMain: '#C42C2C',
  errorDark: '#8C1F1F',

  // orange (Berry uses this token in some surfaces — we keep a muted tone on-brand)
  orangeLight: '#F7E0CC',
  orangeMain: '#D8853A',
  orangeDark: '#A35E20',

  // warning / caution
  warningLight: '#F4E2BD',
  warningMain: '#D89D3A',
  warningDark: '#8E6420',

  // grey / ink scale (aligned with THEME-SPEC.md ink tokens)
  grey50: '#F5F7FA', // surface.canvas
  grey100: '#EEF2F6',
  grey200: '#E3E8EF',
  grey300: '#C8D2DC', // ink.300 — disabled, dividers
  grey500: '#7A8A9A', // ink.500 — muted text, helper copy
  grey600: '#4B5565',
  grey700: '#243B52', // ink.700 — secondary text, table headers
  grey900: '#0B1B2A', // ink.900 — body text on light surfaces

  // ==============================|| WEIGHTRAX DARK MODE ||============================== //
  // Note: dark theme is supported as a token-level swap but is NOT a Day-1 priority.
  // Polish deferred until post-PoC. See THEME-SPEC.md §1.

  // paper & background
  darkPaper: '#0A1623',
  darkBackground: '#11233A', // page canvas; kept darker than darkLevel2 (cards) so panels stand out

  // dark levels
  darkLevel1: '#243B52',
  darkLevel2: '#1A2F47',

  // text variants
  darkTextTitle: '#FFFFFF',
  darkTextPrimary: '#E6EDF3',
  darkTextSecondary: '#A8B5C3',

  // primary dark (interactive blue stays bright)
  darkPrimaryLight: '#CCE7F5',
  darkPrimaryMain: '#0088CC',
  darkPrimaryDark: '#006FA8',
  darkPrimary200: '#66B8DD',
  darkPrimary800: '#004F7A',

  // secondary dark (navy variants)
  darkSecondaryLight: '#D5DEE6',
  darkSecondaryMain: '#1B3A52',
  darkSecondaryDark: '#112B40',
  darkSecondary200: '#7E97AC',
  darkSecondary800: '#0E2334'
};

export default weightrax;
